/**
 * revisionFields — 문제 이력의 **필드**(본문·풀이·정답·라벨·개념·난이도)를 다루는 규칙 한 벌.
 *
 * 계약: docs/exam-builder-v2/BE-20260830-reply-ocr-repromote-history-close.md §2
 * (오너 결정 ②로 이력이 본문 하나에서 여섯으로 늘었다. dev `46f06f4`)
 *
 * ── 이 저장소에 번호줄이 **둘** 있다. 섞으면 화면이 거짓을 말한다 ──
 *
 *   문제 「rev 17」 — 문제 하나에 줄이 **하나뿐**이고 여섯 필드가 그것을 나눠 쓴다.
 *                    계약 §2: 「revision 번호는 **필드 무관하게 문제당 하나씩 증가**」.
 *                    그래서 본문만 걸러 보면 17·12·5 처럼 **번호가 건너뛴다.** 고장이 아니다.
 *   그림 「v4」     — 그림 **하나마다** 따로 1부터 센다(계약 §1-5·§3-4, 다른 API).
 *                    본문 rev 17 과 그림 1의 v4 는 아무 관계가 없다.
 *
 * 접두사를 갈라 둔다(`rev` ↔ `v`) — 표기는 오너 지시(2026-08-30)로 「17판」에서 「rev 17」이 됐고,
 * 그림 쪽 「v4」는 계약이 `version` 이라 그대로 둔다. 그 둘을 섞어 쓰지 않는 것이 이 파일과
 * FigureHistoryList 가 함께 지키는 약속이다.
 *
 * ⚠ `value` 의 **모양**은 계약에 없다. 「작은 필드(answer·labels·concepts·difficulty)만
 * 동봉」이라고만 적혀 있고 라벨·개념이 문자열인지 배열인지 객체 배열인지 쓰여 있지 않다
 * (→ 백엔드 질의). 그래서 describeRevisionValue 는 **네 모양을 전부 견디게** 짰다 —
 * 모르는 모양이 와도 화면이 죽지 않고 사람이 읽을 무언가를 낸다.
 */
import { DIFFICULTY_LABEL } from './problemApi';

/** 화면에 세울 순서. 계약 §2 의 `field` 여섯을 그대로, 사람이 보는 순서로. */
export const REVISION_FIELDS = [
  { key: 'body', name: '본문' },
  { key: 'solution', name: '풀이' },
  { key: 'answer', name: '정답' },
  { key: 'labels', name: '라벨' },
  { key: 'concepts', name: '개념' },
  { key: 'difficulty', name: '난이도' },
];

const NAME_BY_KEY = REVISION_FIELDS.reduce((m, f) => ({ ...m, [f.key]: f.name }), {});

/**
 * 이 판이 어느 필드인가.
 *
 * **`field` 가 없으면 본문이다.** 계약 §2 끝줄이 「기존 판은 전부 `field:"body"` 로 백필됨」
 * 이라고 했고, 그 배포 전 응답에는 이 키가 아예 없다. 없는 것을 「모름」으로 두면 옛 판이
 * 전부 정체불명이 되어 필터에서 사라진다 — 백필이 정한 값을 화면도 따른다.
 */
export function revisionFieldKey(item) {
  const f = item?.field;
  return typeof f === 'string' && f.trim() !== '' ? f : 'body';
}

/**
 * 필드 코드만으로 이름을 찾는다. 판이 **없는** 칸(그 시점에 기록이 아직 없다)에도 이름은 붙여야 해서
 * 필요하다 — 그때는 서버가 준 `field_name` 이 존재하지 않는다.
 */
export function revisionFieldNameByKey(key) {
  return NAME_BY_KEY[key] || key;
}

/** 서버가 준 이름이 늘 이긴다 — 백엔드가 필드를 늘려도 화면이 따라간다(그림 source_name 과 같은 규칙). */
export function revisionFieldName(item) {
  if (item?.field_name) return String(item.field_name);
  const k = revisionFieldKey(item);
  return NAME_BY_KEY[k] || k;
}

/**
 * 본문·풀이인가 — **렌더러가 갈리는 축**이다.
 * 이 둘만 Lexical 문서라 편집기로 그려야 하고, 상세 GET 을 한 번 더 해야 값이 온다
 * (계약 §2: value 는 작은 필드만 동봉). 나머지 넷은 목록에 실려 온 값을 그대로 그린다.
 */
export function isDocRevision(field) {
  return field === 'body' || field === 'solution';
}

/**
 * 「비운 판」인가 — 문서 판의 root 에 자식이 하나도 없다.
 *
 * BE dev 12d5567(회신 BE-20260927-reply-fe-analysis-regenerate.md R1)부터 내용 없는 풀이 저장은
 * 「풀이 비우기」(행 삭제)이고, 이력에는 `{"root":{"children":[]…}}` 판이 남는다. 편집기는 이 모양을
 * 열지 못해(Lexical error #38 — normalizeEditorState 가 미리 거른다) 「형식이 올바르지 않습니다」로 떴다.
 * 고장이 아니라 「비움」이므로 화면이 그렇게 말한다. 그 판으로 되돌리기 = 풀이 비우기다.
 *
 * 편집기가 만드는 빈 문단 하나짜리 옛 판은 여기에 들지 않는다 — 편집기로 열리고 빈 칸으로 보인다.
 * 문자열(계약)·객체(실제로 오는 모양) 둘 다 받는다. 파싱이 안 되는 값은 비운 판이 아니다(모른다).
 */
export function isClearedDoc(content) {
  if (content == null) return false;
  let o = content;
  if (typeof content === 'string') {
    try { o = JSON.parse(content); } catch (_) { return false; }
  }
  const kids = o?.root?.children;
  return Array.isArray(kids) && kids.length === 0;
}

/**
 * 필드별 최신 판 번호 `{ body: 17, answer: 12, … }`.
 *
 * 서버가 `heads` 를 주면 그것이 정본이다(계약 §2). 안 주면 **직접 센다** — 옛 배포분과
 * 섞여 돌 때 [최신] 배지가 통째로 사라지고 [되돌리기]가 최신 판에서도 열리기 때문이다.
 * 여기서 세는 값과 서버 값은 같아야 정상이고, 다르면 서버를 믿는다.
 */
export function revisionHeads(list) {
  const server = list?.heads;
  if (server && typeof server === 'object' && !Array.isArray(server)) return server;
  const out = {};
  (Array.isArray(list?.items) ? list.items : []).forEach((it) => {
    const k = revisionFieldKey(it);
    const r = Number(it?.revision);
    if (!Number.isFinite(r)) return;
    if (out[k] == null || r > out[k]) out[k] = r;
  });
  return out;
}

/**
 * 서버가 `field` 를 실어 주는가.
 *
 * **「아직 정답 판이 없다」와 「서버가 필드를 안 준다」는 다른 일이다.** 화면에는 둘 다
 * 「본문뿐」으로 보이는데, 앞엣것은 정답을 한 번 고치면 풀리고 뒤엣것은 백엔드 배포를 기다려야 한다.
 * 가르지 않으면 사람이 정답을 고쳐 보고 「그래도 안 나온다」로 다시 묻는다.
 *
 * 판정은 **한 판이라도 `field` 를 달고 왔는가**로 한다 — 계약 §2 가 기존 판을 전부
 * `field:"body"` 로 백필했다고 했으므로, 배포됐다면 옛 판에도 이 키가 붙어 온다.
 */
export function isFieldAware(items) {
  return (Array.isArray(items) ? items : []).some(
    (it) => typeof it?.field === 'string' && it.field.trim() !== '',
  );
}

/** 이 이력에 실제로 있는 필드만, 정해진 순서로. 없는 필드의 탭을 내지 않으려고 쓴다. */
export function presentRevisionFields(items) {
  const seen = new Set((Array.isArray(items) ? items : []).map(revisionFieldKey));
  const known = REVISION_FIELDS.filter((f) => seen.has(f.key));
  // 계약에 없는 필드가 오면 뒤에 붙인다 — 백엔드가 늘렸을 때 그 판이 화면에서 사라지지 않게.
  const extra = [...seen].filter((k) => !NAME_BY_KEY[k]).map((k) => ({ key: k, name: k }));
  return [...known, ...extra];
}

/**
 * **그 시점**의 이 필드 값을 담은 판.
 *
 * rev 번호가 필드 무관하게 하나씩 오르므로(계약 §2), 「rev 21 시점의 본문」은 21번 판이 아니라
 * **21 이하에서 가장 큰 본문 판**이다. 이 계산이 없으면 화면은 고른 판의 필드 하나만 보여 주게 되고,
 * 그것은 「그때 문제가 어땠나」가 아니라 「그때 무엇이 바뀌었나」다 — 둘은 다른 물음이고
 * 사람이 이력에서 알고 싶은 것은 앞엣것이다(오너 지적 2026-08-30 「하단 내용을 요약하면 안 되지」).
 *
 * 그 시점에 그 필드 기록이 아직 없으면 null 이다 — 없는 것을 빈 값으로 그리지 않는다.
 */
export function revisionAt(items, field, rev) {
  const upTo = Number(rev);
  if (!Number.isFinite(upTo)) return null;
  let best = null;
  (Array.isArray(items) ? items : []).forEach((it) => {
    if (revisionFieldKey(it) !== field) return;
    const r = Number(it?.revision);
    if (!Number.isFinite(r) || r > upTo) return;
    if (best == null || r > Number(best.revision)) best = it;
  });
  return best;
}

/** 이 필드의 **가장 이른** 판. 이력이 언제부터 남기 시작했는지가 여기서 나온다. */
export function earliestRevisionOf(items, field) {
  let best = null;
  (Array.isArray(items) ? items : []).forEach((it) => {
    if (revisionFieldKey(it) !== field) return;
    const r = Number(it?.revision);
    if (!Number.isFinite(r)) return;
    if (best == null || r < Number(best.revision)) best = it;
  });
  return best;
}

/**
 * 그 시점의 이 필드를 **세 갈래로** 판정한다.
 *
 * ⚠ 이것이 2026-08-30 오너 보고 「옛날 버전으로 돌려보니 풀이가 있었는데 없었다고 판정한다」의
 * 수정이다. 종전에는 `revisionAt` 이 null 이면 그대로 「기록이 없습니다」로 그렸는데,
 * **그건 「풀이가 없었다」가 아니라 「우리가 모른다」다.** 계약 §2 끝줄이 「기존 판은 전부
 * `field:"body"` 로 백필됨」이라 필드별 기록은 나중에 시작한다 — 실제로 그 문제의 풀이 판은
 * rev 18(도입 전 원본), 정답 판은 rev 20 부터다. 그보다 앞선 시점을 고르면 풀이가 멀쩡히
 * 있었는데도 화면이 없었다고 말했다.
 *
 *   exact    — 그 시점(이하)의 판을 찾았다. 이 값이 그때 값이다.
 *   earliest — 그 시점 이전 기록이 없다. **가장 이른 판을 대신 보인다** — 그때 값과 다를 수 있다.
 *   none     — 이 필드의 판이 하나도 없다. 그래도 「풀이가 없다」가 아니라 **「이력이 없다」**다.
 *
 * 안 온 것과 빈 것은 다르다 — 이 저장소가 여러 자리에서 지키는 규칙이고(figureCapabilities·
 * describeRevisionValue), 여기서 한 번 어겼던 것을 되돌린다.
 */
export function revisionStateAt(items, field, rev) {
  const at = revisionAt(items, field, rev);
  if (at) return { at, kind: 'exact' };
  const first = earliestRevisionOf(items, field);
  if (first) return { at: first, kind: 'earliest' };
  return { at: null, kind: 'none' };
}

/**
 * 미리보기에 **늘 세우는** 세 칸(오너 지시 2026-08-30 「본문, 정답, 풀이 모두 원래처럼 보여야 함」).
 * 라벨·개념·난이도는 그 판이 바로 그것일 때만 한 칸 더 붙인다 — 늘 세우면 매번 노이즈가 되고,
 * 안 세우면 라벨 판을 골랐을 때 **바뀐 것이 화면에 없다.**
 */
export const REVISION_PREVIEW_FIELDS = ['body', 'answer', 'solution'];

/**
 * 드롭다운 한 줄의 글자 (오너 지시 2026-08-30 「이 영역을 드롭다운으로 선택하도록」).
 *
 * 목록이던 것을 `<select>` 로 바꾸면서 **배지를 쓸 수 없게 됐다** — option 안에는 글자만 들어간다.
 * 그래서 줄에 있던 것을 `·` 로 잇는다. 순서는 눈으로 짚던 순서 그대로다(번호 → 무엇 → 최신 →
 * 누가·왜 → 값 → 언제). 잃은 배지는 사라지지 않고 **고른 뒤 미리보기 머리줄**로 옮겨 간다.
 *
 * 값은 자른다 — 라벨 한 판이 「정규모의고사테스트,13번,4점,객관식,p.7,2026학년도 …」처럼 길어서
 * 안 자르면 그 한 줄이 드롭다운 폭을 통째로 정한다.
 *
 * sourceText·time 을 **밖에서 받는다**: 출처 이름표는 analysisApi 가, 시각 서식은 화면이 갖고 있다.
 * 여기서 그 둘을 import 하면 이 파일이 API 계층에 묶여 검사하기 나빠진다.
 */
export function revisionOptionLabel(item, { isHead = false, sourceText = '', time = '' } = {}) {
  const key = revisionFieldKey(item);
  const parts = [`rev ${item?.revision}`, revisionFieldName(item)];
  if (isHead) parts.push('최신');
  if (sourceText) parts.push(sourceText);
  const val = isDocRevision(key)
    ? ((item?.image_count ?? 0) > 0 ? `그림 ${item.image_count}` : null)
    : truncate(describeRevisionValue(key, item?.value), 24);
  if (val) parts.push(val);
  if (time) parts.push(time);
  return parts.join(' · ');
}

function truncate(s, n) {
  if (s == null) return null;
  const t = String(s);
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

/**
 * 작은 필드(정답·라벨·개념·난이도)의 값을 **사람이 읽을 한 줄**로.
 * 값이 없으면 null 을 돌려준다 — 「없음」이라고 단정하지 않는다(안 온 것과 빈 것은 다르다).
 */
export function describeRevisionValue(field, value) {
  if (value == null) return null;
  if (field === 'difficulty') {
    const n = Number(value);
    return Number.isFinite(n) ? `${n}${DIFFICULTY_LABEL[n] ? ` (${DIFFICULTY_LABEL[n]})` : ''}` : String(value);
  }
  if (typeof value === 'string') return value.trim() === '' ? null : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const parts = value.map(oneToText).filter((s) => s != null && s !== '');
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return oneToText(value);
}

/** 배열 한 칸 · 객체 하나를 글자로. 이름처럼 생긴 키를 먼저 찾고, 없으면 원문을 보인다. */
function oneToText(v) {
  if (v == null) return null;
  if (typeof v === 'string') return v.trim() === '' ? null : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') {
    const named = v.label_name || v.name || v.concept_name || v.title || v.slug;
    if (named) return String(named);
    try { return JSON.stringify(v); } catch (_) { return null; }
  }
  return null;
}
